import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Calendar, MessageSquare, Images, Save, User, Clock, Image as ImageIcon, Video, FileText, CalendarPlus, Plus, Trash2, Search } from 'lucide-react';
import { useAuth } from '@/contexts/use-auth';
import { useToast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { formatApiError } from '@/services/api';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  getCommunityEvent, updateCommunityEvent, getCommunityEventMessages, updateCommunityEventMessage,
  getEventImages, uploadEventBackground, uploadEventImage, getVimeoContent, getAllContent,
  getCommunityEvents, addContentEventTag, removeContentEventTag,
  addCommunityEventTagToEvent, removeCommunityEventTagFromEvent, Timestamp
} from '@/lib/firebase';
import { logger } from '@/lib/logger';
import { captureHandledException } from '@/lib/sentry';

interface CommunityEvent {
  id: string;
  title: string;
  description?: string;
  type: string;
  tag?: string;
  startDate?: any;
  endDate?: any;
  month?: string;
  url?: string;
  enableMessageBoard?: boolean;
  enableImageUpload?: boolean;
  backgroundImageUrl?: string;
  added?: any;
  status?: 'Scheduled' | 'Current' | 'Completed';
  contentEvents?: string[];
}

interface ContentItem {
  id: string;
  title: string;
  description?: string;
  mediaType: 'vimeo' | 'youtube' | 'article';
  enabled: boolean;
  contentEvents?: string[];
  url?: string;
  thumbnailUrl?: string;
  duration?: string;
  author?: string;
}

interface EventMessage {
  id: string;
  text: string;
  userName?: string;
  userEmail?: string;
  timestamp?: any;
  imageUrl?: string;
  isDisabled?: boolean;
}

const eventSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  type: z.enum(['liveQA', 'stepsChallenge', 'yoga', 'content']),
  tag: z.string().optional(),
  dateMode: z.enum(['month', 'dateRange']).optional(),
  selectedMonth: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  url: z.string().optional(),
  enableMessageBoard: z.boolean(),
  enableImageUpload: z.boolean(),
  backgroundImageUrl: z.string().optional(),
});

type EventFormData = z.infer<typeof eventSchema>;

export default function EventDetails() {
  const [location, navigate] = useLocation();
  const eventId = new URLSearchParams(window.location.search).get('id');
  
  const [event, setEvent] = useState<CommunityEvent | null>(null);
  const [messages, setMessages] = useState<EventMessage[]>([]);
  const [eventImages, setEventImages] = useState<string[]>([]);
  const [linkedVideos, setLinkedVideos] = useState<ContentItem[]>([]);
  const [availableVideos, setAvailableVideos] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [pendingImageUpload, setPendingImageUpload] = useState<{ file: File; field: any } | null>(null);
  const [activeTab, setActiveTab] = useState('details');
  const [isAddVideoModalOpen, setIsAddVideoModalOpen] = useState(false);
  const [videoSearchTerm, setVideoSearchTerm] = useState('');
  const [selectedVideos, setSelectedVideos] = useState<string[]>([]);

  // Articles state
  const [linkedArticles, setLinkedArticles] = useState<ContentItem[]>([]);
  const [availableArticles, setAvailableArticles] = useState<ContentItem[]>([]);
  const [isAddArticleModalOpen, setIsAddArticleModalOpen] = useState(false);
  const [selectedArticles, setSelectedArticles] = useState<string[]>([]);
  const [articleSearchTerm, setArticleSearchTerm] = useState('');

  // Events state
  const [linkedEvents, setLinkedEvents] = useState<CommunityEvent[]>([]);
  const [availableEvents, setAvailableEvents] = useState<CommunityEvent[]>([]);
  const [isAddEventModalOpen, setIsAddEventModalOpen] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [eventSearchTerm, setEventSearchTerm] = useState('');
  
  const { user } = useAuth();
  const { toast } = useToast();

  const form = useForm<EventFormData>({
    resolver: zodResolver(eventSchema),
    defaultValues: {
      title: '',
      description: '',
      type: 'liveQA' as const,
      dateMode: 'dateRange' as const,
      selectedMonth: '',
      startDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
      url: '',
      enableMessageBoard: false,
      enableImageUpload: false,
      backgroundImageUrl: '',
    },
  });

  const watchedType = form.watch('type');
  const watchedDateMode = form.watch('dateMode');

  useEffect(() => {
    if (!eventId || !user) {
      return;
    }

    // Load event details
    const loadEvent = async () => {
      try {
        const eventData = await getCommunityEvent(eventId) as CommunityEvent | null;
        
        if (eventData) {
          setEvent(eventData);
          
          // Populate form with event data
          populateForm(eventData);
        } else {
          toast({
            title: "Event not found",
            description: "The requested event could not be found.",
            variant: "destructive",
          });
          const returnParams = new URLSearchParams(window.location.search).get('returnParams');
          navigate(returnParams ? `/events?${decodeURIComponent(returnParams)}` : '/events');
        }
      } catch (error) {
        logger.error('Error loading event:', error);
        toast({
          title: "Error loading event",
          description: "Failed to load event details.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadEvent();

    // Load messages with polling
    const loadMessages = async () => {
      try {
        const messagesData = await getCommunityEventMessages(eventId) as EventMessage[];
        const sortedMessages = messagesData.sort((a, b) => {
          const aDate = a.timestamp?.toDate?.() || new Date(0);
          const bDate = b.timestamp?.toDate?.() || new Date(0);
          return aDate.getTime() - bDate.getTime();
        });
        setMessages(sortedMessages);
      } catch (error) {
        logger.error('Error loading messages:', error);
      }
    };
    loadMessages();
    const msgTimer = setInterval(loadMessages, 15_000);

    // Load event images from the imageUrls field
    const loadEventImages = async () => {
      try {
        const urls = await getEventImages(eventId);
        setEventImages(urls);
      } catch (error) {
        logger.error('Error loading event images:', error);
      }
    };
    loadEventImages();

    return () => {
      clearInterval(msgTimer);
    };
  }, [eventId, user, toast, navigate]);

  // Load linked videos when event is loaded or tab changes to videos
  useEffect(() => {
    if (event && event.type === 'content' && activeTab === 'videos') {
      loadLinkedVideos();
    }
  }, [event, activeTab]);

  // Load available videos when opening add modal
  useEffect(() => {
    if (isAddVideoModalOpen) {
      loadAvailableVideos();
    }
  }, [isAddVideoModalOpen]);

  // Load linked articles when event is loaded or tab changes to articles
  useEffect(() => {
    if (event && event.type === 'content' && activeTab === 'articles') {
      loadLinkedArticles();
    }
  }, [event, activeTab]);

  // Load available articles when opening add modal
  useEffect(() => {
    if (isAddArticleModalOpen) {
      loadAvailableArticles();
    }
  }, [isAddArticleModalOpen]);

  // Load linked events when event is loaded or tab changes to events
  useEffect(() => {
    if (event && event.type === 'content' && activeTab === 'events') {
      loadLinkedEvents();
    }
  }, [event, activeTab]);

  // Load available events when opening add modal
  useEffect(() => {
    if (isAddEventModalOpen) {
      loadAvailableEvents();
    }
  }, [isAddEventModalOpen]);

  const populateForm = (eventData: CommunityEvent) => {
    // Convert dates for form
    let startDateStr = '';
    let endDateStr = '';
    let startTimeStr = '';
    let endTimeStr = '';
    let dateMode: 'month' | 'dateRange' = 'dateRange';
    let selectedMonth = '';

    if (eventData.month) {
      dateMode = 'month';
      selectedMonth = eventData.month;
    } else if (eventData.startDate) {
      if (eventData.startDate?.toDate) {
        const startDate = eventData.startDate.toDate();
        startDateStr = startDate.toISOString().split('T')[0];
        
        if (eventData.type !== 'stepsChallenge') {
          startTimeStr = startDate.toTimeString().slice(0, 5);
        }
      }
      
      if (eventData.endDate?.toDate) {
        const endDate = eventData.endDate.toDate();
        
        if (eventData.type === 'stepsChallenge') {
          endDateStr = endDate.toISOString().split('T')[0];
        } else {
          endTimeStr = endDate.toTimeString().slice(0, 5);
        }
      }
    }

    form.reset({
      title: eventData.title,
      description: eventData.description || '',
      type: eventData.type as 'liveQA' | 'stepsChallenge' | 'yoga',
      dateMode,
      selectedMonth,
      startDate: startDateStr,
      endDate: endDateStr,
      startTime: startTimeStr,
      endTime: endTimeStr,
      url: eventData.url || '',
      enableMessageBoard: eventData.enableMessageBoard || false,
      enableImageUpload: eventData.enableImageUpload || false,
      backgroundImageUrl: eventData.backgroundImageUrl || '',
    });
  };

  const generateMonthOptions = () => {
    const months = [];
    const currentYear = new Date().getFullYear();
    
    for (let year = currentYear; year <= currentYear + 2; year++) {
      for (let month = 0; month < 12; month++) {
        const date = new Date(year, month, 1);
        const monthString = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        months.push({
          value: `${year}-${String(month + 1).padStart(2, '0')}`,
          label: monthString
        });
      }
    }
    return months;
  };

  const handleImageUpload = async (file: File): Promise<string> => {
    setUploadingImage(true);
    try {
      return await uploadEventBackground(file);
    } catch (error) {
      logger.error('Error uploading image:', error);
      throw new Error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const retryImageUpload = async () => {
    if (!pendingImageUpload) return;
    const { file, field } = pendingImageUpload;
    try {
      const downloadURL = await handleImageUpload(file);
      field.onChange(downloadURL);
      setPendingImageUpload(null);
      toast({
        title: "Image uploaded",
        description: "Image has been uploaded successfully.",
      });
    } catch (error) {
      captureHandledException(error, "event-details.upload-image");
      toast({
        title: "Upload failed",
        description: formatApiError(error, "upload image"),
        variant: "destructive",
        action: (
          <ToastAction altText="Retry" onClick={retryImageUpload}>
            Retry
          </ToastAction>
        ),
      });
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>, field: any) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please select an image file.",
        variant: "destructive",
      });
      return;
    }

    try {
      const downloadURL = await handleImageUpload(file);
      field.onChange(downloadURL);
      setPendingImageUpload(null);
      toast({
        title: "Image uploaded",
        description: "Image has been uploaded successfully.",
      });
    } catch (error) {
      captureHandledException(error, "event-details.retry-image-upload");
      setPendingImageUpload({ file, field });
      toast({
        title: "Upload failed",
        description: formatApiError(error, "upload image"),
        variant: "destructive",
        action: (
          <ToastAction altText="Retry" onClick={() => retryImageUpload()}>
            Retry
          </ToastAction>
        ),
      });
    }
  };

  const handleToggleMessageDisabled = async (messageId: string, isDisabled: boolean) => {
    try {
      await updateCommunityEventMessage(messageId, { isDisabled });
      
      toast({
        title: isDisabled ? "Message disabled" : "Message enabled",
        description: `Message has been ${isDisabled ? 'disabled' : 'enabled'} successfully.`,
      });
    } catch (error) {
      logger.error('Error updating message status:', error);
      toast({
        title: "Error updating message",
        description: formatApiError(error, "update message status"),
        variant: "destructive",
      });
    }
  };

  // Load linked videos for Content events
  const loadLinkedVideos = async () => {
    if (!event?.tag || event.type !== 'content') return;
    try {
      const all = await getVimeoContent() as ContentItem[];
      setLinkedVideos(all.filter(v => (v as any).contentEvents?.includes(event.tag)));
    } catch (error) {
      logger.error('Error loading linked videos:', error);
    }
  };

  // Load all available videos for selection
  const loadAvailableVideos = async () => {
    if (!event?.tag || event.type !== 'content') return;
    try {
      const all = await getVimeoContent() as ContentItem[];
      setAvailableVideos(all);
    } catch (error) {
      logger.error('Error loading available videos:', error);
    }
  };

  // Add videos to event
  const handleAddVideos = async () => {
    if (!event?.tag || selectedVideos.length === 0) return;
    
    try {
      for (const videoId of selectedVideos) {
        await addContentEventTag(videoId, event.tag!);
      }
      
      toast({
        title: "Videos added",
        description: `${selectedVideos.length} video(s) have been added to this event.`,
      });
      
      setSelectedVideos([]);
      setIsAddVideoModalOpen(false);
      await loadLinkedVideos(); // Refresh the linked videos list
    } catch (error) {
      logger.error('Error adding videos:', error);
      toast({
        title: "Error adding videos",
        description: formatApiError(error, "add videos"),
        variant: "destructive",
      });
    }
  };

  // Remove video from event
  const handleRemoveVideo = async (videoId: string) => {
    if (!event?.tag) return;
    
    try {
      await removeContentEventTag(videoId, event.tag!);
      
      toast({
        title: "Video removed",
        description: "Video has been removed from this event.",
      });
      
      await loadLinkedVideos(); // Refresh the linked videos list
    } catch (error) {
      logger.error('Error removing video:', error);
      toast({
        title: "Error removing video",
        description: formatApiError(error, "remove video"),
        variant: "destructive",
      });
    }
  };

  // Load linked articles for the current event
  const loadLinkedArticles = async () => {
    if (!event?.tag || event.type !== 'content') return;
    try {
      const all = await getAllContent() as ContentItem[];
      setLinkedArticles(all.filter(a => a.mediaType === 'article' && (a as any).contentEvents?.includes(event.tag)));
    } catch (error) {
      logger.error('Error loading linked articles:', error);
    }
  };

  // Load all available articles for adding
  const loadAvailableArticles = async () => {
    if (!event?.tag || event.type !== 'content') return;
    try {
      const all = await getAllContent() as ContentItem[];
      setAvailableArticles(all.filter(a => a.mediaType === 'article'));
    } catch (error) {
      logger.error('Error loading available articles:', error);
    }
  };

  // Add selected articles to event
  const handleAddArticles = async () => {
    if (!event?.tag || selectedArticles.length === 0) return;
    try {
      await Promise.all(selectedArticles.map(articleId => addContentEventTag(articleId, event.tag!)));
      
      toast({
        title: "Success",
        description: `${selectedArticles.length} article(s) added to event.`,
      });
      
      setIsAddArticleModalOpen(false);
      setSelectedArticles([]);
      setArticleSearchTerm('');
      await loadLinkedArticles();
    } catch (error) {
      logger.error('Error adding articles:', error);
      toast({
        title: "Error adding articles",
        description: formatApiError(error, "add articles"),
        variant: "destructive",
      });
    }
  };

  // Remove article from event
  const handleRemoveArticle = async (articleId: string) => {
    if (!event?.tag) return;
    try {
      await removeContentEventTag(articleId, event.tag!);
      
      toast({
        title: "Success",
        description: "Article removed from event.",
      });
      
      await loadLinkedArticles();
    } catch (error) {
      logger.error('Error removing article:', error);
      toast({
        title: "Error removing article",
        description: formatApiError(error, "remove article"),
        variant: "destructive",
      });
    }
  };

  // Check if two date ranges overlap at all
  const dateRangesOverlap = (start1: any, end1: any, start2: any, end2: any) => {
    try {
      // Handle null/undefined dates
      if (!start1 || !end1 || !start2 || !end2) return false;
      
      // Convert Firestore Timestamps to Date objects for comparison
      const convertToDate = (dateValue: any): Date => {
        if (dateValue?.toDate) {
          return dateValue.toDate();
        } else if (typeof dateValue === 'string') {
          return new Date(dateValue);
        } else {
          return new Date(dateValue);
        }
      };
      
      const date1Start = convertToDate(start1);
      const date1End = convertToDate(end1);
      const date2Start = convertToDate(start2);
      const date2End = convertToDate(end2);
      
      // Check for invalid dates
      if (isNaN(date1Start.getTime()) || isNaN(date1End.getTime()) || 
          isNaN(date2Start.getTime()) || isNaN(date2End.getTime())) {
        return false;
      }
      
      // Two date ranges overlap if: start1 <= end2 AND start2 <= end1
      const overlaps = date1Start <= date2End && date2Start <= date1End;
      
      return overlaps;
    } catch (error) {
      logger.error('Error in dateRangesOverlap:', error);
      return false;
    }
  };

  // Load events linked to the current event
  const loadLinkedEvents = async () => {
    if (!event?.tag || event.type !== 'content') return;
    try {
      const allEvents = await getCommunityEvents() as CommunityEvent[];
      const linkedEvents = allEvents.filter(otherEvent => 
        otherEvent.type !== 'content' && otherEvent.contentEvents?.includes(event.tag || '')
      );
      setLinkedEvents(linkedEvents);
    } catch (error) {
      logger.error('Error loading linked events:', error);
    }
  };

  // Load all events that overlap with current event's date range
  const loadAvailableEvents = async () => {
    if (!event?.tag || event.type !== 'content') return;
    if (!event.startDate || !event.endDate) return;
    
    try {
      const allEvents = await getCommunityEvents() as CommunityEvent[];
      
      // Filter for non-Content events with overlapping date ranges
      const overlappingEvents = allEvents.filter(otherEvent => {
        if (otherEvent.id === event.id) return false; // Don't include self
        if (otherEvent.type === 'content') return false; // Don't include other content events
        if (!otherEvent.startDate || !otherEvent.endDate) return false;
        
        return dateRangesOverlap(
          event.startDate, event.endDate,
          otherEvent.startDate, otherEvent.endDate
        );
      });
      
      setAvailableEvents(overlappingEvents);
    } catch (error) {
      logger.error('Error loading available events:', error);
    }
  };

  // Add selected events to current event
  const handleAddEvents = async () => {
    if (!event?.tag || selectedEvents.length === 0) return;
    try {
      await Promise.all(selectedEvents.map(evId => addCommunityEventTagToEvent(evId, event.tag!)));
      
      toast({
        title: "Success",
        description: `${selectedEvents.length} event(s) linked to this content event.`,
      });
      
      setIsAddEventModalOpen(false);
      setSelectedEvents([]);
      setEventSearchTerm('');
      await loadLinkedEvents();
    } catch (error) {
      logger.error('Error adding events:', error);
      toast({
        title: "Error linking events",
        description: formatApiError(error, "link events"),
        variant: "destructive",
      });
    }
  };

  // Remove event link from current event
  const handleRemoveEvent = async (evId: string) => {
    if (!event?.tag) return;
    try {
      await removeCommunityEventTagFromEvent(evId, event.tag!);
      
      toast({
        title: "Success",
        description: "Event unlinked from this content event.",
      });
      
      await loadLinkedEvents();
    } catch (error) {
      logger.error('Error removing event link:', error);
      toast({
        title: "Error unlinking event",
        description: formatApiError(error, "unlink event"),
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async (data: EventFormData) => {
    if (!eventId) return;

    try {
      let startDate: Timestamp | null = null;
      let endDate: Timestamp | null = null;
      let monthField: string | null = null;

      if (data.type === 'stepsChallenge' || data.type === 'content') {
        if (data.dateMode === 'month' && data.selectedMonth) {
          const [year, month] = data.selectedMonth.split('-');
          const startOfMonth = new Date(parseInt(year), parseInt(month) - 1, 1);
          const endOfMonth = new Date(parseInt(year), parseInt(month), 0);
          startDate = Timestamp.fromDate(startOfMonth);
          endDate = Timestamp.fromDate(endOfMonth);
          monthField = data.selectedMonth;
        } else if (data.startDate && data.endDate) {
          startDate = Timestamp.fromDate(new Date(data.startDate));
          endDate = Timestamp.fromDate(new Date(data.endDate));
        }
      } else {
        if (data.startDate && data.startTime) {
          const startDateTime = new Date(`${data.startDate}T${data.startTime}`);
          startDate = Timestamp.fromDate(startDateTime);
        }
        if (data.startDate && data.endTime) {
          const endDateTime = new Date(`${data.startDate}T${data.endTime}`);
          endDate = Timestamp.fromDate(endDateTime);
        }
      }

      const updateData = {
        title: data.title,
        description: data.description || '',
        type: data.type,
        startDate,
        endDate,
        month: monthField,
        url: data.type !== 'stepsChallenge' ? data.url || '' : undefined,
        enableMessageBoard: data.enableMessageBoard,
        enableImageUpload: data.enableImageUpload,
        backgroundImageUrl: data.backgroundImageUrl || '',
      };

      // Remove undefined values
      const cleanUpdateData = Object.fromEntries(
        Object.entries(updateData).filter(([_, value]) => value !== undefined)
      );

      await updateCommunityEvent(eventId, cleanUpdateData);
      
      toast({
        title: "Event updated",
        description: "Event has been updated successfully.",
      });
      
      // Reload the event data
      const reloaded = await getCommunityEvent(eventId) as CommunityEvent | null;
      if (reloaded) {
        setEvent(reloaded);
      }
    } catch (error) {
      logger.error('Error updating event:', error);
      toast({
        title: "Error updating event",
        description: formatApiError(error, "update event"),
        variant: "destructive",
      });
    }
  };

  const formatDate = (date: any): string => {
    if (!date) return 'No date set';
    
    try {
      if (date?.toDate) {
        return date.toDate().toLocaleDateString();
      }
      return new Date(date).toLocaleDateString();
    } catch (error) {
      captureHandledException(error, "event-details.parse-date");
      return 'Invalid date';
    }
  };

  const formatDateTime = (date: any): string => {
    if (!date) return 'No date set';
    
    try {
      if (date?.toDate) {
        return date.toDate().toLocaleString();
      }
      return new Date(date).toLocaleString();
    } catch (error) {
      captureHandledException(error, "event-details.parse-date");
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <div className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-center text-slate-600">Loading event details...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-slate-600">Event not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const returnParams = new URLSearchParams(window.location.search).get('returnParams');
                navigate(returnParams ? `/events?${decodeURIComponent(returnParams)}` : '/events');
              }}
              className="flex items-center gap-2"
              data-testid="button-back"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Events
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Calendar className="h-6 w-6" />
                {event.title}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="secondary">{event.type}</Badge>
                {event.month && <span className="text-slate-600 text-sm">{event.month}</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Tabbed Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {event.type === 'content' ? (
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="details" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Event Details
              </TabsTrigger>
              <TabsTrigger value="videos" className="flex items-center gap-2">
                <Video className="h-4 w-4" />
                Videos ({linkedVideos.length})
              </TabsTrigger>
              <TabsTrigger value="articles" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Articles ({linkedArticles.length})
              </TabsTrigger>
              <TabsTrigger value="events" className="flex items-center gap-2">
                <CalendarPlus className="h-4 w-4" />
                Events ({linkedEvents.length})
              </TabsTrigger>
            </TabsList>
          ) : (
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="details" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Event Details
              </TabsTrigger>
              <TabsTrigger value="messages" className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Messages ({messages.length})
              </TabsTrigger>
              <TabsTrigger value="images" className="flex items-center gap-2">
                <Images className="h-4 w-4" />
                Images ({eventImages.length})
              </TabsTrigger>
            </TabsList>
          )}

          {/* Event Details Tab */}
          <TabsContent value="details" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Edit Event Details</CardTitle>
                <CardDescription>
                  Update the event information and settings.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                    {/* Basic Information */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-slate-900">Basic Information</h3>
                      
                      <FormField
                        control={form.control}
                        name="title"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Title *</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="Enter event title" data-testid="input-title" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Description</FormLabel>
                            <FormControl>
                              <Textarea {...field} placeholder="Enter event description" data-testid="input-description" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="type"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Event Type *</FormLabel>
                            <FormControl>
                              <Input 
                                {...field} 
                                disabled 
                                className="bg-slate-100 text-slate-600" 
                                data-testid="input-type-disabled"
                                value={field.value === 'liveQA' ? 'Live Q&A' : 
                                       field.value === 'stepsChallenge' ? 'Steps Challenge' : 
                                       field.value === 'yoga' ? 'Yoga' : 
                                       field.value === 'content' ? 'Content Event' : field.value}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Date/Time Configuration */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-slate-900">Date & Time</h3>
                      
                      {(watchedType === 'stepsChallenge' || watchedType === 'content') && (
                        <FormField
                          control={form.control}
                          name="dateMode"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Date Selection Mode</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger data-testid="select-date-mode">
                                    <SelectValue placeholder="Select date mode" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="month">Select Month</SelectItem>
                                  <SelectItem value="dateRange">Date Range</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}

                      {(watchedType === 'stepsChallenge' || watchedType === 'content') && watchedDateMode === 'month' && (
                        <FormField
                          control={form.control}
                          name="selectedMonth"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Select Month</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger data-testid="select-month">
                                    <SelectValue placeholder="Select month" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {generateMonthOptions().map((month) => (
                                    <SelectItem key={month.value} value={month.value}>
                                      {month.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}

                      {(watchedType === 'stepsChallenge' || watchedType === 'content') && watchedDateMode === 'dateRange' && (
                        <div className="grid grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="startDate"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Start Date</FormLabel>
                                <FormControl>
                                  <Input type="date" {...field} data-testid="input-start-date" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="endDate"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>End Date</FormLabel>
                                <FormControl>
                                  <Input type="date" {...field} data-testid="input-end-date" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      )}

                      {(watchedType === 'liveQA' || watchedType === 'yoga') && (
                        <>
                          <FormField
                            control={form.control}
                            name="startDate"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Event Date</FormLabel>
                                <FormControl>
                                  <Input type="date" {...field} data-testid="input-event-date" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <div className="grid grid-cols-2 gap-4">
                            <FormField
                              control={form.control}
                              name="startTime"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Start Time</FormLabel>
                                  <FormControl>
                                    <Input type="time" {...field} data-testid="input-start-time" />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={form.control}
                              name="endTime"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>End Time</FormLabel>
                                  <FormControl>
                                    <Input type="time" {...field} data-testid="input-end-time" />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                        </>
                      )}
                    </div>

                    {/* URL Field (not for steps challenges or Content) */}
                    {watchedType !== 'stepsChallenge' && watchedType !== 'content' && (
                      <div className="space-y-4">
                        <h3 className="font-semibold text-slate-900">Event URL</h3>
                        <FormField
                          control={form.control}
                          name="url"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>URL</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="Enter event URL" data-testid="input-url" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    )}

                    {/* Settings - Hidden for Content type */}
                    {watchedType !== 'content' && (
                      <div className="space-y-4">
                        <h3 className="font-semibold text-slate-900">Settings</h3>
                        
                        <FormField
                          control={form.control}
                          name="enableMessageBoard"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                              <div className="space-y-0.5">
                                <FormLabel className="text-base">Enable Messages</FormLabel>
                                <div className="text-sm text-slate-600">
                                  Allow participants to send messages during the event
                                </div>
                              </div>
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  data-testid="switch-enable-messages"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="enableImageUpload"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                              <div className="space-y-0.5">
                                <FormLabel className="text-base">Enable Images</FormLabel>
                                <div className="text-sm text-slate-600">
                                  Allow participants to share images during the event
                                </div>
                              </div>
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  data-testid="switch-enable-images"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    )}

                    {/* Image Upload */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-slate-900">Event Image</h3>
                      <FormField
                        control={form.control}
                        name="backgroundImageUrl"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Background Image</FormLabel>
                            <FormControl>
                              <div className="space-y-4">
                                <Input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => handleFileChange(e, field)}
                                  disabled={uploadingImage}
                                  data-testid="input-image-upload"
                                />
                                {uploadingImage && (
                                  <div className="text-sm text-slate-600">Uploading image...</div>
                                )}
                                {field.value && (
                                  <div className="flex items-center gap-2 text-sm text-green-600">
                                    <ImageIcon className="h-4 w-4" />
                                    Image uploaded successfully
                                  </div>
                                )}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-end pt-4">
                      <Button type="submit" disabled={uploadingImage} data-testid="button-save" className="flex items-center gap-2">
                        <Save className="h-4 w-4" />
                        {uploadingImage ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab - Hidden for Content events */}
          {event.type !== 'content' && (
            <TabsContent value="messages" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Event Messages</CardTitle>
                  <CardDescription>
                    Messages posted by participants during the event.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {messages.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-slate-900 mb-2">No messages yet</h3>
                      <p className="text-slate-600">
                        Participants haven't posted any messages for this event.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {messages.map((message) => (
                        <div key={message.id} className={`border rounded-lg p-4 ${message.isDisabled ? 'bg-red-50 border-red-200' : 'bg-slate-50'}`} data-testid={`message-${message.id}`}>
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-slate-500" />
                              <span className="font-medium text-slate-900">
                                {message.userName || message.userEmail || 'Anonymous'}
                              </span>
                              {message.isDisabled && (
                                <Badge variant="destructive" className="text-xs">Disabled</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-1 text-sm text-slate-500">
                                <Clock className="h-3 w-3" />
                                {formatDateTime(message.timestamp)}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-slate-600">Disabled</span>
                                <Switch
                                  checked={message.isDisabled || false}
                                  onCheckedChange={(checked) => handleToggleMessageDisabled(message.id, checked)}
                                  data-testid={`switch-disable-message-${message.id}`}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="text-slate-700 mb-3 whitespace-pre-wrap break-words">
                            {message.text}
                          </div>
                          {message.imageUrl && (
                            <div className="mt-3">
                              <img
                                src={message.imageUrl}
                                alt="Message attachment"
                                className="w-20 h-20 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => window.open(message.imageUrl, '_blank')}
                                data-testid={`message-image-${message.id}`}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Images Tab - Hidden for Content events */}
          {event.type !== 'content' && (
            <TabsContent value="images" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Event Images</CardTitle>
                  <CardDescription>
                    Images uploaded by participants during the event.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {eventImages.length === 0 ? (
                    <div className="text-center py-12">
                      <Images className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-slate-900 mb-2">No images yet</h3>
                      <p className="text-slate-600">
                        No images have been uploaded for this event.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {eventImages.map((imageUrl, index) => (
                        <div key={index} className="aspect-square rounded-lg overflow-hidden border bg-slate-100" data-testid={`image-${index}`}>
                          <img
                            src={imageUrl}
                            alt={`Event image ${index + 1}`}
                            className="w-full h-full object-cover hover:scale-105 transition-transform cursor-pointer"
                            onClick={() => window.open(imageUrl, '_blank')}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Content Event Tabs */}
          {event.type === 'content' && (
            <>
              {/* Videos Tab */}
              <TabsContent value="videos" className="space-y-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <div>
                      <CardTitle>Videos</CardTitle>
                      <CardDescription>
                        Video content associated with this content event.
                      </CardDescription>
                    </div>
                    <Button 
                      onClick={() => setIsAddVideoModalOpen(true)}
                      className="flex items-center gap-2"
                      data-testid="button-add-videos"
                    >
                      <Plus className="h-4 w-4" />
                      Add Videos
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {linkedVideos.length === 0 ? (
                      <div className="text-center py-12">
                        <Video className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-slate-900 mb-2">No videos linked</h3>
                        <p className="text-slate-600 mb-4">
                          No videos have been linked to this content event yet.
                        </p>
                        <Button 
                          onClick={() => setIsAddVideoModalOpen(true)}
                          variant="outline"
                          className="flex items-center gap-2"
                        >
                          <Plus className="h-4 w-4" />
                          Add Videos
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {linkedVideos.map((video) => (
                          <div key={video.id} className="flex items-center justify-between p-4 border rounded-lg" data-testid={`video-${video.id}`}>
                            <div className="flex items-center gap-4">
                              <div className="flex-shrink-0">
                                <Video className="h-8 w-8 text-slate-500" />
                              </div>
                              <div>
                                <h4 className="font-medium text-slate-900">{video.title}</h4>
                                {video.description && (
                                  <p className="text-sm text-slate-600 line-clamp-2">{video.description}</p>
                                )}
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge variant="outline" className="text-xs">
                                    {video.mediaType.toUpperCase()}
                                  </Badge>
                                  {video.duration && (
                                    <span className="text-xs text-slate-500">{video.duration}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRemoveVideo(video.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`button-remove-video-${video.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Articles Tab */}
              <TabsContent value="articles" className="space-y-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <div>
                      <CardTitle>Articles</CardTitle>
                      <CardDescription>
                        Article content associated with this content event.
                      </CardDescription>
                    </div>
                    <Button 
                      onClick={() => setIsAddArticleModalOpen(true)}
                      className="flex items-center gap-2"
                      data-testid="button-add-articles"
                    >
                      <Plus className="h-4 w-4" />
                      Add Articles
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {linkedArticles.length === 0 ? (
                      <div className="text-center py-12">
                        <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-slate-900 mb-2">No articles linked</h3>
                        <p className="text-slate-600 mb-4">
                          No articles have been linked to this content event yet.
                        </p>
                        <Button 
                          onClick={() => setIsAddArticleModalOpen(true)}
                          variant="outline"
                          className="flex items-center gap-2"
                        >
                          <Plus className="h-4 w-4" />
                          Add Articles
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {linkedArticles.map((article) => (
                          <div key={article.id} className="flex items-center justify-between p-4 border rounded-lg" data-testid={`article-${article.id}`}>
                            <div className="flex items-center gap-4">
                              <div className="flex-shrink-0">
                                <FileText className="h-8 w-8 text-slate-500" />
                              </div>
                              <div>
                                <h4 className="font-medium text-slate-900">{article.title}</h4>
                                {article.description && (
                                  <p className="text-sm text-slate-600 line-clamp-2">{article.description}</p>
                                )}
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge variant="outline" className="text-xs">
                                    ARTICLE
                                  </Badge>
                                  {article.author && (
                                    <span className="text-xs text-slate-500">by {article.author}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRemoveArticle(article.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`button-remove-article-${article.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Events Tab */}
              <TabsContent value="events" className="space-y-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <div>
                      <CardTitle>Events</CardTitle>
                      <CardDescription>
                        Related events with overlapping date ranges.
                      </CardDescription>
                    </div>
                    <Button 
                      onClick={() => setIsAddEventModalOpen(true)}
                      className="flex items-center gap-2"
                      data-testid="button-add-events"
                    >
                      <Plus className="h-4 w-4" />
                      Link Events
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {linkedEvents.length === 0 ? (
                      <div className="text-center py-12">
                        <CalendarPlus className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-slate-900 mb-2">No events linked</h3>
                        <p className="text-slate-600 mb-4">
                          No overlapping events have been linked to this content event yet.
                        </p>
                        <Button 
                          onClick={() => setIsAddEventModalOpen(true)}
                          variant="outline"
                          className="flex items-center gap-2"
                        >
                          <Plus className="h-4 w-4" />
                          Link Events
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {linkedEvents.map((linkedEvent) => (
                          <div key={linkedEvent.id} className="flex items-center justify-between p-4 border rounded-lg" data-testid={`event-${linkedEvent.id}`}>
                            <div className="flex items-center gap-4">
                              <div className="flex-shrink-0">
                                <Calendar className="h-8 w-8 text-slate-500" />
                              </div>
                              <div>
                                <h4 className="font-medium text-slate-900">{linkedEvent.title}</h4>
                                {linkedEvent.description && (
                                  <p className="text-sm text-slate-600 line-clamp-2">{linkedEvent.description}</p>
                                )}
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge variant="outline" className="text-xs">
                                    {linkedEvent.type?.toUpperCase() || 'EVENT'}
                                  </Badge>
                                  {linkedEvent.startDate && linkedEvent.endDate && (
                                    <span className="text-xs text-slate-500">
                                      {linkedEvent.startDate.toDate ? 
                                        `${linkedEvent.startDate.toDate().toLocaleDateString()} - ${linkedEvent.endDate.toDate().toLocaleDateString()}` :
                                        'Date range'
                                      }
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRemoveEvent(linkedEvent.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`button-remove-event-${linkedEvent.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </>
          )}
        </Tabs>

        {/* Add Videos Modal */}
        <Dialog open={isAddVideoModalOpen} onOpenChange={setIsAddVideoModalOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Videos to Event</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search videos..."
                  value={videoSearchTerm}
                  onChange={(e) => setVideoSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="input-video-search"
                />
              </div>

              {/* Video List */}
              <div className="max-h-96 overflow-y-auto border rounded-lg">
                {availableVideos
                  .filter(video => 
                    !video.contentEvents?.includes(event?.tag || '') &&
                    (videoSearchTerm === '' || 
                     video.title.toLowerCase().includes(videoSearchTerm.toLowerCase()) ||
                     video.description?.toLowerCase().includes(videoSearchTerm.toLowerCase()))
                  )
                  .map((video) => (
                    <div key={video.id} className="flex items-center space-x-3 p-4 hover:bg-slate-50 border-b last:border-b-0" data-testid={`available-video-${video.id}`}>
                      <Checkbox
                        id={video.id}
                        checked={selectedVideos.includes(video.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedVideos(prev => [...prev, video.id]);
                          } else {
                            setSelectedVideos(prev => prev.filter(id => id !== video.id));
                          }
                        }}
                        data-testid={`checkbox-video-${video.id}`}
                      />
                      <div className="flex-shrink-0">
                        <Video className="h-6 w-6 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-slate-900 truncate">{video.title}</h4>
                        {video.description && (
                          <p className="text-sm text-slate-600 line-clamp-2">{video.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {video.mediaType.toUpperCase()}
                          </Badge>
                          {video.duration && (
                            <span className="text-xs text-slate-500">{video.duration}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                
                {availableVideos.filter(video => 
                  !video.contentEvents?.includes(event?.tag || '') &&
                  (videoSearchTerm === '' || 
                   video.title.toLowerCase().includes(videoSearchTerm.toLowerCase()) ||
                   video.description?.toLowerCase().includes(videoSearchTerm.toLowerCase()))
                ).length === 0 && (
                  <div className="text-center py-8">
                    <Video className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">No videos available</h3>
                    <p className="text-slate-600">
                      {videoSearchTerm ? 'No videos match your search.' : 'All available videos are already linked to this event.'}
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsAddVideoModalOpen(false);
                    setSelectedVideos([]);
                    setVideoSearchTerm('');
                  }}
                  data-testid="button-cancel-add-videos"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddVideos}
                  disabled={selectedVideos.length === 0}
                  data-testid="button-confirm-add-videos"
                >
                  Add Selected ({selectedVideos.length})
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Articles Modal */}
        <Dialog open={isAddArticleModalOpen} onOpenChange={setIsAddArticleModalOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Articles to Event</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search articles..."
                  value={articleSearchTerm}
                  onChange={(e) => setArticleSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="input-article-search"
                />
              </div>

              {/* Article List */}
              <div className="max-h-96 overflow-y-auto border rounded-lg">
                {availableArticles
                  .filter(article => 
                    !article.contentEvents?.includes(event?.tag || '') &&
                    (articleSearchTerm === '' || 
                     article.title.toLowerCase().includes(articleSearchTerm.toLowerCase()) ||
                     article.description?.toLowerCase().includes(articleSearchTerm.toLowerCase()))
                  )
                  .map((article) => (
                    <div key={article.id} className="flex items-center space-x-3 p-4 hover:bg-slate-50 border-b last:border-b-0" data-testid={`available-article-${article.id}`}>
                      <Checkbox
                        id={article.id}
                        checked={selectedArticles.includes(article.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedArticles(prev => [...prev, article.id]);
                          } else {
                            setSelectedArticles(prev => prev.filter(id => id !== article.id));
                          }
                        }}
                        data-testid={`checkbox-article-${article.id}`}
                      />
                      <div className="flex-shrink-0">
                        <FileText className="h-6 w-6 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-slate-900 truncate">{article.title}</h4>
                        {article.description && (
                          <p className="text-sm text-slate-600 line-clamp-2">{article.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            ARTICLE
                          </Badge>
                          {article.author && (
                            <span className="text-xs text-slate-500">by {article.author}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                
                {availableArticles.filter(article => 
                  !article.contentEvents?.includes(event?.tag || '') &&
                  (articleSearchTerm === '' || 
                   article.title.toLowerCase().includes(articleSearchTerm.toLowerCase()) ||
                   article.description?.toLowerCase().includes(articleSearchTerm.toLowerCase()))
                ).length === 0 && (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">No articles available</h3>
                    <p className="text-slate-600">
                      {articleSearchTerm ? 'No articles match your search.' : 'All available articles are already linked to this event.'}
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsAddArticleModalOpen(false);
                    setSelectedArticles([]);
                    setArticleSearchTerm('');
                  }}
                  data-testid="button-cancel-add-articles"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddArticles}
                  disabled={selectedArticles.length === 0}
                  data-testid="button-confirm-add-articles"
                >
                  Add Selected ({selectedArticles.length})
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Events Modal */}
        <Dialog open={isAddEventModalOpen} onOpenChange={setIsAddEventModalOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Link Events to Content Event</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search events..."
                  value={eventSearchTerm}
                  onChange={(e) => setEventSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="input-event-search"
                />
              </div>

              {/* Event List */}
              <div className="max-h-96 overflow-y-auto border rounded-lg">
                {availableEvents
                  .filter(availableEvent => 
                    !availableEvent.contentEvents?.includes(event?.tag || '') &&
                    (eventSearchTerm === '' || 
                     availableEvent.title.toLowerCase().includes(eventSearchTerm.toLowerCase()) ||
                     availableEvent.description?.toLowerCase().includes(eventSearchTerm.toLowerCase()))
                  )
                  .map((availableEvent) => (
                    <div key={availableEvent.id} className="flex items-center space-x-3 p-4 hover:bg-slate-50 border-b last:border-b-0" data-testid={`available-event-${availableEvent.id}`}>
                      <Checkbox
                        id={availableEvent.id}
                        checked={selectedEvents.includes(availableEvent.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedEvents(prev => [...prev, availableEvent.id]);
                          } else {
                            setSelectedEvents(prev => prev.filter(id => id !== availableEvent.id));
                          }
                        }}
                        data-testid={`checkbox-event-${availableEvent.id}`}
                      />
                      <div className="flex-shrink-0">
                        <Calendar className="h-6 w-6 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-slate-900 truncate">{availableEvent.title}</h4>
                        {availableEvent.description && (
                          <p className="text-sm text-slate-600 line-clamp-2">{availableEvent.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {availableEvent.type?.toUpperCase() || 'EVENT'}
                          </Badge>
                          {availableEvent.startDate && availableEvent.endDate && (
                            <span className="text-xs text-slate-500">
                              {availableEvent.startDate.toDate ? 
                                `${availableEvent.startDate.toDate().toLocaleDateString()} - ${availableEvent.endDate.toDate().toLocaleDateString()}` :
                                'Date range'
                              }
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                
                {availableEvents.filter(availableEvent => 
                  !availableEvent.contentEvents?.includes(event?.tag || '') &&
                  (eventSearchTerm === '' || 
                   availableEvent.title.toLowerCase().includes(eventSearchTerm.toLowerCase()) ||
                   availableEvent.description?.toLowerCase().includes(eventSearchTerm.toLowerCase()))
                ).length === 0 && (
                  <div className="text-center py-8">
                    <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">No events available</h3>
                    <p className="text-slate-600">
                      {eventSearchTerm ? 'No events match your search.' : 'No overlapping events are available to link, or all overlapping events are already linked.'}
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsAddEventModalOpen(false);
                    setSelectedEvents([]);
                    setEventSearchTerm('');
                  }}
                  data-testid="button-cancel-add-events"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddEvents}
                  disabled={selectedEvents.length === 0}
                  data-testid="button-confirm-add-events"
                >
                  Link Selected ({selectedEvents.length})
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}